import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import type {
  Task,
  TaskType,
  CreateTaskInput,
  UpdateTaskInput,
  FilterCriteria,
  ApiResponse,
} from '../types/task';
import { useActiveYear } from '../contexts/ActiveYearContext';

const TASKS_KEY = 'tasks';

/**
 * Resolve a FilterCriteria — including its set of combinable quick filters — into
 * the concrete criteria that drive the server query.
 *
 * Quick filters AND together with each other and with the explicit base criteria
 * (e.g. Mine + This Week = my tasks due this week; Overdue + Milestones = overdue
 * milestones). They are resolved here, client-side, so the server contract stays
 * the simple flat one it already understands; the only genuinely new server param
 * is multi-domain (`domains`).
 *
 * `currentUserId` is needed so the "Mine" quick filter can resolve to the caller.
 */
export function resolveCriteria(
  filters?: FilterCriteria,
  currentUserId?: string,
): {
  /** Concrete criteria to serialize for the server (sans the milestone type filter). */
  serverCriteria: FilterCriteria;
  /** Client-side type filter (milestones quick filter), applied to the response. */
  typeFilter: TaskType | undefined;
} {
  const quick = new Set(filters?.quickFilters ?? []);

  // Start from the explicit base criteria, then layer the active quick filters.
  const out: FilterCriteria = { ...filters };
  delete out.quickFilters;

  let typeFilter: TaskType | undefined = filters?.type;

  if (quick.size) {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    if (quick.has('mine') && currentUserId) {
      // Intersect with any explicit assignees rather than overwrite.
      const existing = out.assigneeIds;
      out.assigneeIds =
        existing && existing.length ? existing.filter((id) => id === currentUserId) : [currentUserId];
    }

    if (quick.has('overdue')) {
      // Open tasks due before today. ANDs with an explicit dueBefore by taking
      // the earlier bound; ANDs with status by restricting to 'todo'.
      out.dueBefore = minDate(out.dueBefore, today.toISOString());
      out.status = ['todo'];
    }

    if (quick.has('thisWeek')) {
      const endOfWeek = new Date(today);
      endOfWeek.setDate(today.getDate() + (7 - today.getDay()));
      out.dueAfter = maxDate(out.dueAfter, today.toISOString());
      out.dueBefore = minDate(out.dueBefore, endOfWeek.toISOString());
    }

    if (quick.has('milestones')) {
      typeFilter = 'milestone';
    }
  }

  return { serverCriteria: out, typeFilter };
}

/** Return the earlier of two ISO dates (ignoring undefined). */
function minDate(a?: string, b?: string): string | undefined {
  if (!a) return b;
  if (!b) return a;
  return a <= b ? a : b;
}

/** Return the later of two ISO dates (ignoring undefined). */
function maxDate(a?: string, b?: string): string | undefined {
  if (!a) return b;
  if (!b) return a;
  return a >= b ? a : b;
}

/**
 * Build query string from filter criteria
 */
function buildQueryString(filters?: FilterCriteria, year?: number | 'all'): string {
  const params = new URLSearchParams();

  if (filters?.assigneeIds?.length) {
    params.set('assigneeIds', filters.assigneeIds.join(','));
  }
  if (filters?.status?.length) {
    params.set('statuses', filters.status.join(','));
  }
  // Multi-domain takes precedence; fall back to the legacy single domain.
  if (filters?.domains?.length) {
    params.set('domains', filters.domains.join(','));
  } else if (filters?.domain) {
    params.set('domain', filters.domain);
  }
  if (filters?.dueBefore) {
    params.set('dueBefore', filters.dueBefore);
  }
  if (filters?.dueAfter) {
    params.set('dueAfter', filters.dueAfter);
  }
  if (filters?.hasNoDueDate !== undefined) {
    params.set('hasNoDueDate', String(filters.hasNoDueDate));
  }
  if (filters?.searchText) {
    params.set('searchText', filters.searchText);
  }
  if (year !== undefined) {
    params.set('year', String(year));
  }

  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * Hook to list tasks, scoped to the active year by default. Tasks do not
 * carry over between years.
 */
export function useTasks(filters?: FilterCriteria, currentUserId?: string) {
  const { year: activeYear } = useActiveYear();
  const { serverCriteria, typeFilter } = resolveCriteria(filters, currentUserId);
  const queryString = buildQueryString(serverCriteria, activeYear ?? undefined);

  return useQuery({
    // Key on the raw filters + user so toggling a quick filter (or who "Mine"
    // resolves to) refetches.
    queryKey: [TASKS_KEY, filters, currentUserId, activeYear],
    queryFn: async () => {
      const response = await api.get<ApiResponse<Task[]>>(`/tasks${queryString}`);
      const tasks = response.data;
      if (typeFilter) {
        return tasks.filter((t) => t.type === typeFilter);
      }
      return tasks;
    },
    enabled: activeYear !== null,
  });
}

/**
 * Hook to get a single task by ID
 */
export function useTask(id: string) {
  return useQuery({
    queryKey: [TASKS_KEY, id],
    queryFn: async () => {
      const response = await api.get<ApiResponse<Task>>(`/tasks/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
}

/**
 * Hook to create a new task with optimistic updates
 */
export function useCreateTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateTaskInput) => {
      const response = await api.post<ApiResponse<Task>>('/tasks', input);
      return response.data;
    },
    onMutate: async (input) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: [TASKS_KEY] });

      // Snapshot current queries for rollback
      const previousQueries = queryClient.getQueriesData<Task[]>({ queryKey: [TASKS_KEY] });

      // Create optimistic task with temporary ID
      const optimisticTask: Task = {
        id: `temp-${Date.now()}`,
        title: input.title,
        description: input.description,
        status: input.status || 'todo',
        dueDate: input.dueDate,
        assigneeIds: input.assigneeIds || [],
        labelIds: [],
        dependencyIds: input.dependencyIds || [],
        createdBy: 'current-user',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        version: 1,
        type: input.type ?? 'task',
        domain: input.domain,
        milestoneId: input.milestoneId,
        dueDateRelativeTo: input.dueDateRelativeTo,
      };

      // Optimistically add to all task queries
      queryClient.setQueriesData<Task[]>(
        { queryKey: [TASKS_KEY] },
        (old) => {
          if (!old) return [optimisticTask];
          return [optimisticTask, ...old];
        }
      );

      return { previousQueries, optimisticTask };
    },
    onError: (_err, _input, context) => {
      // Rollback on error
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: () => {
      // Refetch to sync with server
      queryClient.invalidateQueries({ queryKey: [TASKS_KEY] });
    },
  });
}

/**
 * Hook to update an existing task with optimistic updates
 */
export function useUpdateTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...input }: UpdateTaskInput & { id: string }) => {
      const response = await api.put<ApiResponse<Task>>(`/tasks/${id}`, input);
      return response.data;
    },
    onMutate: async ({ id, ...updates }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: [TASKS_KEY] });
      await queryClient.cancelQueries({ queryKey: [TASKS_KEY, id] });

      // Snapshot current state for rollback
      const previousQueries = queryClient.getQueriesData<Task[]>({ queryKey: [TASKS_KEY] });
      const previousTask = queryClient.getQueryData<Task>([TASKS_KEY, id]);

      // UpdateTaskInput allows null for "clear" semantics on linkage fields,
      // but cached Task objects only hold string | undefined. Coerce before merging.
      const optimisticPatch = {
        ...updates,
        milestoneId: updates.milestoneId ?? undefined,
        dueDateRelativeTo: updates.dueDateRelativeTo ?? undefined,
        updatedAt: new Date().toISOString(),
      };

      // Optimistically update all task lists — bump version so rapid
      // sequential mutations don't send a stale version.
      queryClient.setQueriesData<Task[]>(
        { queryKey: [TASKS_KEY] },
        (old) => {
          if (!old) return old;
          return old.map((task) =>
            task.id === id ? { ...task, ...optimisticPatch, version: task.version + 1 } : task
          );
        }
      );

      // Optimistically update single task query
      if (previousTask) {
        queryClient.setQueryData<Task>([TASKS_KEY, id], {
          ...previousTask,
          ...optimisticPatch,
          version: previousTask.version + 1,
        });
      }

      return { previousQueries, previousTask };
    },
    onError: (_err, { id }, context) => {
      // Rollback on error
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      if (context?.previousTask) {
        queryClient.setQueryData([TASKS_KEY, id], context.previousTask);
      }
    },
    onSettled: (_data, _err, { id }) => {
      // Refetch to sync with server
      queryClient.invalidateQueries({ queryKey: [TASKS_KEY] });
      queryClient.invalidateQueries({ queryKey: [TASKS_KEY, id] });
    },
  });
}

/**
 * Applies a partial update to every task in `tasks` concurrently, using each
 * task's own `version` for optimistic-locking. Runs all mutations in parallel
 * and invalidates the tasks query once when they all settle.
 *
 * Usage:
 *   const bulkUpdate = useBulkUpdateTasks();
 *   await bulkUpdate(selectedTasks, { assigneeIds: ['user-1'] });
 */
export function useBulkUpdateTasks() {
  const updateTask = useUpdateTask();

  return async (
    tasks: { id: string; version: number }[],
    patch: Omit<UpdateTaskInput, 'version'>,
  ) => {
    await Promise.all(
      tasks.map((task) =>
        updateTask.mutateAsync({ id: task.id, version: task.version, ...patch }),
      ),
    );
  };
}

/**
 * Hook to delete a task with optimistic updates
 */
export function useDeleteTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/tasks/${id}`);
      return id;
    },
    onMutate: async (id) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: [TASKS_KEY] });
      await queryClient.cancelQueries({ queryKey: [TASKS_KEY, id] });

      // Snapshot current state for rollback
      const previousQueries = queryClient.getQueriesData<Task[]>({ queryKey: [TASKS_KEY] });
      const previousTask = queryClient.getQueryData<Task>([TASKS_KEY, id]);

      // Optimistically remove from all task lists
      queryClient.setQueriesData<Task[]>(
        { queryKey: [TASKS_KEY] },
        (old) => {
          if (!old) return old;
          return old.filter((task) => task.id !== id);
        }
      );

      // Remove single task query
      queryClient.removeQueries({ queryKey: [TASKS_KEY, id] });

      return { previousQueries, previousTask };
    },
    onError: (_err, id, context) => {
      // Rollback on error
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      if (context?.previousTask) {
        queryClient.setQueryData([TASKS_KEY, id], context.previousTask);
      }
    },
    onSettled: () => {
      // Refetch to sync with server
      queryClient.invalidateQueries({ queryKey: [TASKS_KEY] });
    },
  });
}
